import React, { useState } from 'react';
import { css } from '@emotion/react';
import TopNav from '@/components/ui/TopNav/TopNav';
import EmailCard from '@/components/ui/EmailCard/EmailCard';
import SideNav from '@/components/ui/SideNav/SideNav';
import ProductsView from '@/components/ui/ProductsView/ProductsView';
import DiscountsView from '@/components/ui/DiscountsView/DiscountsView';
import AudienceView from '@/components/ui/AudienceView/AudienceView';
import AnalyticsView from '@/components/ui/AnalyticsView/AnalyticsView';
import { useAppShell } from '@/hooks/useAppShell';
import { useSideNav } from '@/hooks/useSideNav';
import { useNavPage } from '@/hooks/useNavPage';

const page = css`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #f8f7f7;
`;

const pageShell = css`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #f8f7f7;
  padding: 8px;
  gap: 8px;
`;

const pageSideNav = css`
  display: flex;
  min-height: 100vh;
  background: #f8f7f7;
`;

const sideNavMain = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 0;
  overflow: auto;
  background: #f8f7f7;
  padding: 32px 40px;
  gap: 48px;
`;

const content = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
  padding: 56px 0;
  flex: 1;
  overflow: auto;

  @media (max-width: 1169px) {
    padding: 48px 16px;
  }

  @media (max-width: 767px) {
    gap: 40px;
  }
`;

const contentShell = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
  padding: 56px 0;
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  background: #fff;
  border-radius: 10px;
  box-shadow: var(
    --grn-shadow-s,
    0px 1px 1px -0.1px rgba(0, 0, 0, 0.05),
    0px 1.98px 3.06px -0.36px rgba(0, 0, 0, 0.05)
  );

  @media (max-width: 1169px) {
    padding: 48px 16px;
  }

  @media (max-width: 767px) {
    gap: 40px;
  }
`;

const header = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 1140px;
  max-width: 100%;

  @media (max-width: 1169px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 24px;
    align-items: stretch;
  }
`;

const tabs = css`
  display: flex;
  gap: 24px;
  align-items: baseline;

  @media (max-width: 767px) {
    justify-content: center;
    gap: 16px;
  }
`;

const tabActive = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 24px;
  line-height: 28.63px;
  color: #10100f;
  white-space: nowrap;
  cursor: pointer;
  margin: 0;

  @media (max-width: 767px) {
    font-size: 18px;
    line-height: 23.2px;
  }
`;

const tabInactive = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 24px;
  line-height: 28.63px;
  color: rgba(61, 46, 41, 0.52);
  white-space: nowrap;
  cursor: pointer;
  margin: 0;
  transition: color var(--grn-transition-fast, 200ms) ease;

  &:hover {
    color: rgba(61, 46, 41, 0.72);
  }

  @media (max-width: 767px) {
    font-size: 18px;
    line-height: 23.2px;
  }
`;

const actions = css`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    justify-content: space-between;
  }
`;

const searchSortStatus = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const textButton = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 36px;
  padding: 0 8px;
  border-radius: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--grn-color-content, #10100f);
  white-space: nowrap;
  transition: background var(--grn-transition-fast, 200ms) ease;

  svg {
    color: var(--grn-color-icon, rgba(61, 46, 41, 0.52));
  }

  &:hover {
    background: rgba(58, 58, 44, 0.06);
  }
`;

const iconToggleGroup = css`
  display: flex;
  align-items: center;
  overflow: hidden;
`;

const iconToggle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--grn-color-icon, rgba(61, 46, 41, 0.52));
  transition: background var(--grn-transition-fast, 200ms) ease;

  &:hover {
    background: rgba(58, 58, 44, 0.06);
  }
`;

const iconToggleActive = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  color: #10100f;
  background: rgba(58, 58, 44, 0.08);
`;

const newEmailButton = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--grn-space-s, 8px);
  height: var(--grn-control-m, 40px);
  padding: 0 var(--grn-space-m, 16px);
  background: var(--grn-color-shade12, #10100f);
  border: 1px solid transparent;
  border-radius: var(--grn-radius-s, 6px);
  cursor: pointer;
  font-family: 'FlodeskSans', sans-serif;
  font-weight: var(--grn-weight-medium, 500);
  font-size: var(--grn-text-m, 14px);
  line-height: 20px;
  color: var(--grn-color-shade2, #f9f9f6);
  white-space: nowrap;
  transition: background var(--grn-transition-fast, 200ms) ease;

  svg {
    color: #a39a97;
  }

  &:hover {
    background: var(--grn-color-shade11, #2a2a28);
  }

  @media (max-width: 767px) {
    display: none;
  }
`;

const sortButton = css`
  display: none;

  @media (max-width: 767px) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 36px;
    padding: 0 8px;
    border-radius: 6px;
    background: transparent;
    border: none;
    cursor: pointer;
    font-family: 'FlodeskSans', sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: var(--grn-color-content, #10100f);
    white-space: nowrap;
    transition: background var(--grn-transition-fast, 200ms) ease;

    svg {
      color: var(--grn-color-icon, rgba(61, 46, 41, 0.52));
    }

    &:hover {
      background: rgba(58, 58, 44, 0.06);
    }
  }
`;

const emailGrid = css`
  display: flex;
  flex-wrap: wrap;
  gap: 56px;
  width: 1140px;
  max-width: 100%;

  @media (max-width: 1169px) {
    width: 100%;
    gap: 40px;
  }
`;

const emailTable = css`
  display: flex;
  flex-direction: column;
  width: 1140px;
  max-width: 100%;
  background: #fff;
  border-radius: 10px;
  padding: 32px 32px 16px;

  @media (max-width: 1169px) {
    width: 100%;
    padding: 24px 16px 8px;
  }
`;

const emailTableCalendar = css`
  background: transparent;
  border-radius: 0;
  padding: 0;

  @media (max-width: 1169px) {
    padding: 0;
  }
`;

const calendarWrapper = css`
  display: flex;
  flex-direction: column;
  width: 1140px;
  max-width: 100%;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
`;

const calendarHeader = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
`;

const calendarHeaderTitle = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: var(--grn-weight-medium, 500);
  font-size: 18px;
  line-height: 23.2px;
  color: #241a17;
`;

const calendarControls = css`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const calendarTodayGroup = css`
  display: flex;
  align-items: center;
`;

const calendarChevronButton = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 8px;
  border-radius: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--grn-color-icon, rgba(57, 48, 45, 0.52));
  transition: background var(--grn-transition-fast, 200ms) ease;
  &:hover {
    background: rgba(58, 58, 44, 0.06);
  }
`;

const calendarTextButton = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 8px;
  border-radius: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: rgba(57, 48, 45, 0.52);
  transition: background var(--grn-transition-fast, 200ms) ease;
  &:hover {
    background: rgba(58, 58, 44, 0.06);
  }
`;

const calendarTextButtonActive = css`
  background: rgba(58, 47, 44, 0.15);
  color: #251b18;
  &:hover {
    background: rgba(58, 47, 44, 0.15);
  }
`;

const weekdaysRow = css`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border-bottom: 1px solid rgba(60, 50, 47, 0.12);
  height: 46px;
`;

const weekdayCell = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 12px;
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #251b18;
`;

const monthGrid = css`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`;

const dayCell = css`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 112px;
  padding: 8px 4px;
  border: 0.5px solid rgba(60, 50, 47, 0.12);
  background: #fff;
  overflow: hidden;
  gap: 4px;
`;

const dayCellOutside = css`
  background: #f3f2f1;
`;

const dayNumber = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 17.8px;
  color: #251b18;
`;

const dayNumberOutside = css`
  color: #887f7c;
`;

const dayNumberSelected = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 100px;
  background: var(--grn-color-selection);
  color: #fff;
  font-weight: 500;
`;

const tableHeaderRow = css`
  display: flex;
  align-items: center;
  height: 36px;
`;

const tableHeaderCell = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #241a17;
  padding: 0 8px;
`;

const emailHeaderCell = css`
  padding-left: 0;
`;

const colActions = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-width: 132px;

  @media (max-width: 767px) {
    min-width: 44px;
  }
`;

const rowIconButton = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 8px;
  border-radius: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--grn-color-icon, rgba(61, 46, 41, 0.52));
  transition: background var(--grn-transition-fast, 200ms) ease;

  &:hover {
    background: rgba(58, 58, 44, 0.06);
  }
`;

const hiddenIcon = css`
  visibility: hidden;
  pointer-events: none;
`;

const mobileHidden = css`
  @media (max-width: 767px) {
    display: none;
  }
`;

const tableRow = css`
  display: flex;
  align-items: center;
  height: 80px;
  border-bottom: 1px solid rgba(60, 50, 47, 0.12);
`;

const colEmail = css`
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 16px;
  padding-right: 40px;
`;

const colStatus = css`
  width: 240px;
  padding-left: 8px;
  padding-right: 24px;

  @media (max-width: 767px) {
    width: 180px;
  }
`;

const colNumeric = css`
  width: 120px;
  padding-left: 8px;
  padding-right: 24px;
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #241a17;

  @media (max-width: 1169px) {
    display: none;
  }
`;

const mutedCell = css`
  color: rgba(57, 48, 45, 0.52);
`;

const thumbnail = css`
  width: 48px;
  height: 48px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
  border: 0.75px solid rgba(60, 50, 47, 0.12);
`;

const rowTitle = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: var(--grn-weight-medium, 500);
  font-size: 14px;
  line-height: 20px;
  color: #241a17;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const tableBadgeBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 112px;
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17.8px;
  white-space: nowrap;
`;

const badgeSent = css`
  background: #241a17;
  color: #fff;
  border: 1px solid #241a17;
`;

const badgeScheduled = css`
  background: #f4f4e3;
  color: #251b18;
`;

const badgeDraft = css`
  background: transparent;
  color: rgba(61, 46, 41, 0.52);
  border: 1px solid rgba(55, 49, 47, 0.2);
`;

function formatBadgeLabel(status: EmailStatus, detail: string): string {
  if (status === 'sent') {
    const match = detail.match(/on (\w+) (\d+)/);
    if (match) return `Sent on ${match[1]} ${match[2]}`;
    return `Sent · ${detail}`;
  }
  if (status === 'scheduled') return `Scheduled for ${detail}`;
  return `Draft ${detail.toLowerCase()}`;
}

function getBadgeStyle(status: EmailStatus) {
  if (status === 'sent') return [tableBadgeBase, badgeSent];
  if (status === 'scheduled') return [tableBadgeBase, badgeScheduled];
  return [tableBadgeBase, badgeDraft];
}

const paginationWrapper = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const pageButton = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  background: transparent;
  color: rgba(61, 46, 41, 0.52);
  transition:
    background var(--grn-transition-fast, 200ms) ease,
    color var(--grn-transition-fast, 200ms) ease;

  &:hover {
    background: rgba(58, 58, 44, 0.06);
    color: #10100f;
  }
`;

const pageButtonActive = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  background: rgba(58, 58, 44, 0.08);
  color: #10100f;
`;

const arrowButton = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  background: transparent;
  color: rgba(61, 46, 41, 0.52);
  transition:
    background var(--grn-transition-fast, 200ms) ease,
    color var(--grn-transition-fast, 200ms) ease;

  &:hover {
    background: rgba(58, 58, 44, 0.06);
    color: #10100f;
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
    &:hover {
      background: transparent;
      color: rgba(61, 46, 41, 0.52);
    }
  }
`;

const emailNames = [
  'Welcome to the club',
  'Fresh episode is out',
  'Your order has shipped',
  'New collection drop',
  'Weekly roundup',
  'Flash sale — 40% off',
  'Course enrollment open',
  'Holiday gift guide',
  'Behind the scenes',
  'Product launch day',
  'Thank you for subscribing',
  'Your cart misses you',
  'Monthly newsletter',
  'Exclusive members sale',
  'Spring lookbook',
  'Podcast recap',
  'Community spotlight',
  'VIP early access',
  'Year in review',
  'Tips & tutorials',
  'Birthday surprise inside',
  'Referral bonus unlocked',
  'Back in stock alert',
  'Seasonal favorites',
  'Webinar invitation',
  'Digital download ready',
  'End of season clearance',
];

const images = [
  '/images/emails/email-1.png',
  '/images/emails/email-2.png',
  '/images/emails/email-3.png',
  '/images/emails/email-4.png',
  '/images/emails/email-5.png',
  '/images/emails/email-6.png',
];

type EmailStatus = 'sent' | 'scheduled' | 'draft';

type EmailData = {
  id: string;
  title: string;
  image: string;
  status: EmailStatus;
  statusDetail: string;
};

function generateEmails(): EmailData[] {
  const statuses: EmailStatus[] = [
    'draft',
    'draft',
    'scheduled',
    'scheduled',
    'sent',
    'sent',
    'sent',
    'sent',
    'sent',
  ];

  return emailNames.map((name, i) => {
    const status = statuses[i % statuses.length];
    let statusDetail = '';

    if (status === 'sent') {
      const subscribers = 50 + Math.floor(Math.random() * 500);
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const month = months[Math.floor(Math.random() * 12)];
      const day = 1 + Math.floor(Math.random() * 28);
      statusDetail = `${subscribers} subscribers on ${month} ${day}`;
    } else if (status === 'scheduled') {
      const months = ['Mar', 'Apr', 'May'];
      const month = months[Math.floor(Math.random() * 3)];
      const day = 1 + Math.floor(Math.random() * 28);
      const hour = 8 + Math.floor(Math.random() * 10);
      const min = Math.floor(Math.random() * 60)
        .toString()
        .padStart(2, '0');
      const ampm = hour >= 12 ? 'pm' : 'am';
      const displayHour = hour > 12 ? hour - 12 : hour;
      statusDetail = `${month} ${day} at ${displayHour}:${min}${ampm}`;
    } else {
      const hours = ['30 minutes', '1 hour', '2 hours', '5 hours', '1 day', '3 days'];
      statusDetail = `Last edited ${hours[Math.floor(Math.random() * hours.length)]} ago`;
    }

    return {
      id: `email-${i}`,
      title: name,
      image: images[i % images.length],
      status,
      statusDetail,
    };
  });
}

const generatedEmails = generateEmails();
const allEmails = generatedEmails.slice(0, Math.ceil(generatedEmails.length / 2));
const PER_PAGE = 9;
const TOTAL_PAGES = Math.ceil(allEmails.length / PER_PAGE);

const TODAY = new Date();
const CAL_YEAR = TODAY.getFullYear();
const CAL_MONTH = TODAY.getMonth();
const TODAY_DATE = TODAY.getDate();
const CAL_MONTH_NAME = TODAY.toLocaleString('en-US', { month: 'long' });

function getMonthGridDays(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const prevMonthName = new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'short' });
  const nextMonthName = new Date(year, month + 1, 1).toLocaleString('en-US', { month: 'short' });

  type Cell = {
    date: number;
    outside: boolean;
    inPast: boolean;
    label: string;
    fullDate: Date;
  };
  const cells: Cell[] = [];

  for (let i = firstDay - 1; i >= 0; i -= 1) {
    const d = daysInPrevMonth - i;
    const fullDate = new Date(year, month - 1, d);
    cells.push({
      date: d,
      outside: true,
      inPast: fullDate < today,
      label: i === firstDay - 1 ? `${prevMonthName} ${d}` : String(d),
      fullDate,
    });
  }

  for (let d = 1; d <= daysInMonth; d += 1) {
    const fullDate = new Date(year, month, d);
    cells.push({
      date: d,
      outside: false,
      inPast: fullDate < today,
      label: String(d),
      fullDate,
    });
  }

  let nextDay = 1;
  while (cells.length % 7 !== 0 || cells.length < 35) {
    const fullDate = new Date(year, month + 1, nextDay);
    cells.push({
      date: nextDay,
      outside: true,
      inPast: fullDate < today,
      label: nextDay === 1 ? `${nextMonthName} 1` : String(nextDay),
      fullDate,
    });
    nextDay += 1;
    if (cells.length >= 42) break;
  }
  return cells;
}

type ViewMode = 'grid' | 'list' | 'calendar';

export default function Emails() {
  const [isShell] = useAppShell();
  const [isSideNav] = useSideNav();
  const [navPage] = useNavPage();
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [listHasBackground, setListHasBackground] = useState(false);

  const startIdx = (currentPage - 1) * PER_PAGE;
  const pageEmails = allEmails.slice(startIdx, startIdx + PER_PAGE);

  function renderContent() {
    if (navPage === 'products') return <ProductsView />;
    if (navPage === 'discounts') return <DiscountsView />;
    if (navPage === 'audience') return <AudienceView />;
    if (navPage === 'analytics') return <AnalyticsView />;
    return (
      <>
        <div css={header}>
          <div css={tabs}>
            <p css={tabActive}>My emails</p>
            <p css={tabInactive}>Folders</p>
          </div>

          <div css={actions}>
            <div css={searchSortStatus}>
              <button css={textButton} type="button">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <circle cx="7.5" cy="7.5" r="6" stroke="currentColor" strokeWidth="1.5" />
                  <path
                    d="M12 12L16.5 16.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                Search
              </button>
              <button css={sortButton} type="button">
                Sort
                <svg width="10" height="18" viewBox="0 0 10 18" fill="none">
                  <path
                    d="M1 7L5 11L9 7"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button css={textButton} type="button">
                Status
                <svg width="10" height="18" viewBox="0 0 10 18" fill="none">
                  <path
                    d="M1 7L5 11L9 7"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div css={iconToggleGroup}>
              <button
                css={viewMode === 'grid' ? iconToggleActive : iconToggle}
                type="button"
                aria-label="Grid view"
                onClick={() => setViewMode('grid')}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <rect
                    x="1"
                    y="1"
                    width="16"
                    height="16"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.4"
                  />
                </svg>
              </button>
              <button
                css={viewMode === 'list' ? iconToggleActive : iconToggle}
                type="button"
                aria-label="List view"
                onClick={() => setViewMode('list')}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <line
                    x1="1"
                    y1="5"
                    x2="17"
                    y2="5"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                  <line
                    x1="1"
                    y1="9"
                    x2="17"
                    y2="9"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                  <line
                    x1="1"
                    y1="13"
                    x2="17"
                    y2="13"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              <button
                css={viewMode === 'calendar' ? iconToggleActive : iconToggle}
                type="button"
                aria-label="Calendar view"
                onClick={() => setViewMode('calendar')}
              >
                <svg width="16" height="17" viewBox="0 0 16 17" fill="none">
                  <path
                    d="M11 0C11.4142 0 11.75 0.335786 11.75 0.75V1.75H13.75C14.9926 1.75 16 2.75736 16 4V14C16 15.2426 14.9926 16.25 13.75 16.25H2.25C1.00736 16.25 0 15.2426 0 14V4C1.93277e-07 2.75736 1.00736 1.75 2.25 1.75H4.25V0.75C4.25 0.335786 4.58579 0 5 0C5.41421 0 5.75 0.335786 5.75 0.75V1.75H10.25V0.75C10.25 0.335786 10.5858 0 11 0ZM1.5 14C1.5 14.4142 1.83579 14.75 2.25 14.75H13.75C14.1642 14.75 14.5 14.4142 14.5 14V7.5H1.5V14ZM2.25 3.25C1.83579 3.25 1.5 3.58579 1.5 4V6H14.5V4C14.5 3.58579 14.1642 3.25 13.75 3.25H2.25Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>

            <button
              css={newEmailButton}
              type="button"
              onClick={() => setListHasBackground(prev => !prev)}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M7 1V13M1 7H13"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              New email
            </button>
          </div>
        </div>

        {viewMode === 'grid' && (
          <div css={emailGrid}>
            {pageEmails.map(email => (
              <EmailCard
                key={email.id}
                title={email.title}
                image={email.image}
                status={email.status}
                statusDetail={email.statusDetail}
              />
            ))}
          </div>
        )}

        {viewMode === 'calendar' && (
          <div css={calendarWrapper}>
            <div css={calendarHeader}>
              <p css={calendarHeaderTitle}>
                {CAL_MONTH_NAME} {CAL_YEAR}
              </p>
              <div css={calendarControls}>
                <div css={calendarTodayGroup}>
                  <button css={calendarChevronButton} type="button" aria-label="Previous month">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path
                        d="M11 4L7 9L11 14"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button css={calendarTextButton} type="button">
                    Today
                  </button>
                  <button css={calendarChevronButton} type="button" aria-label="Next month">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path
                        d="M7 4L11 9L7 14"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
                <button css={calendarTextButton} type="button">
                  Week
                </button>
                <button css={[calendarTextButton, calendarTextButtonActive]} type="button">
                  Month
                </button>
              </div>
            </div>
            <div css={weekdaysRow}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <div key={d} css={weekdayCell}>
                  {d}
                </div>
              ))}
            </div>
            <div css={monthGrid}>
              {getMonthGridDays(CAL_YEAR, CAL_MONTH).map(cell => {
                const isSelected = !cell.outside && cell.date === TODAY_DATE;
                return (
                  <div
                    key={`${cell.outside ? 'o' : 'i'}-${cell.fullDate.toISOString()}`}
                    css={[dayCell, cell.inPast && dayCellOutside]}
                  >
                    <span
                      css={[
                        dayNumber,
                        (cell.outside || cell.inPast) && dayNumberOutside,
                        isSelected && dayNumberSelected,
                      ]}
                    >
                      {cell.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {viewMode === 'list' && (
          <div css={[emailTable, !listHasBackground && emailTableCalendar]}>
            <div css={tableHeaderRow}>
              <div css={[colEmail, tableHeaderCell, emailHeaderCell]}>Emails</div>
              <div css={[colStatus, tableHeaderCell]}>Status</div>
              <div css={[colNumeric, tableHeaderCell]}>Recipients</div>
              <div css={[colNumeric, tableHeaderCell]}>Open rate</div>
              <div css={[colNumeric, tableHeaderCell]}>Click rate</div>
              <div css={colActions} />
            </div>
            {pageEmails.map(email => {
              const isSent = email.status === 'sent';
              return (
                <div key={email.id} css={tableRow}>
                  <div css={colEmail}>
                    <img css={thumbnail} src={email.image} alt="" />
                    <span css={rowTitle}>{email.title}</span>
                  </div>
                  <div css={colStatus}>
                    <span css={getBadgeStyle(email.status)}>
                      {formatBadgeLabel(email.status, email.statusDetail)}
                    </span>
                  </div>
                  <div css={[colNumeric, !isSent && mutedCell]}>
                    {isSent ? (50 + Math.floor(Math.random() * 10000)).toLocaleString() : '—'}
                  </div>
                  <div css={[colNumeric, !isSent && mutedCell]}>
                    {isSent ? `${30 + Math.floor(Math.random() * 60)}%` : '—'}
                  </div>
                  <div css={[colNumeric, !isSent && mutedCell]}>
                    {isSent ? `${1 + Math.floor(Math.random() * 30)}%` : '—'}
                  </div>
                  <div css={colActions}>
                    <button
                      css={[rowIconButton, mobileHidden, !isSent && hiddenIcon]}
                      type="button"
                      aria-label="View in browser"
                    >
                      <svg width="17" height="18" viewBox="0 0 17 18" fill="none">
                        <path
                          d="M8.5 0.5C13.1944 0.5 17 4.30558 17 9C17 13.6944 13.1944 17.5 8.5 17.5C3.80558 17.5 0 13.6944 0 9C0 4.30558 3.80558 0.5 8.5 0.5ZM8.5 2C4.63401 2 1.5 5.13401 1.5 9C1.5 12.866 4.63401 16 8.5 16C12.366 16 15.5 12.866 15.5 9C15.5 5.13401 12.366 2 8.5 2ZM8.5 3.5C8.91421 3.5 9.25 3.83579 9.25 4.25V9C9.25 9.19891 9.17098 9.38968 9.03033 9.53033L6.53033 12.0303C6.23744 12.3232 5.76256 12.3232 5.46967 12.0303C5.17678 11.7374 5.17678 11.2626 5.46967 10.9697L7.75 8.68934V4.25C7.75 3.83579 8.08579 3.5 8.5 3.5Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                    <button
                      css={[rowIconButton, mobileHidden, !isSent && hiddenIcon]}
                      type="button"
                      aria-label="Analytics"
                    >
                      <svg width="12" height="17" viewBox="0 0 12 17" fill="none">
                        <path
                          d="M1.5 16.25C1.5 16.6642 1.16421 17 0.75 17C0.335787 17 0 16.6642 0 16.25L0 10.25C0 9.83579 0.335786 9.5 0.75 9.5C1.16421 9.5 1.5 9.83579 1.5 10.25L1.5 16.25ZM6.75 16.25C6.75 16.6642 6.41421 17 6 17C5.58579 17 5.25 16.6642 5.25 16.25L5.25 0.75C5.25 0.335786 5.58579 0 6 0C6.41421 0 6.75 0.335786 6.75 0.75L6.75 16.25ZM12 16.25C12 16.6642 11.6642 17 11.25 17C10.8358 17 10.5 16.6642 10.5 16.25L10.5 4.25293C10.5 3.83872 10.8358 3.50293 11.25 3.50293C11.6642 3.50293 12 3.83872 12 4.25293L12 16.25Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                    <button css={rowIconButton} type="button" aria-label="More options">
                      <svg width="15" height="18" viewBox="0 0 15 18" fill="none">
                        <circle cx="2" cy="9" r="1.5" fill="currentColor" />
                        <circle cx="7.5" cy="9" r="1.5" fill="currentColor" />
                        <circle cx="13" cy="9" r="1.5" fill="currentColor" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {viewMode !== 'calendar' && (
          <div css={paginationWrapper}>
            <button
              css={arrowButton}
              type="button"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
              aria-label="Previous page"
            >
              <svg width="10" height="18" viewBox="0 0 10 18" fill="none">
                <path
                  d="M9 5L5 9L9 13"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {Array.from({ length: TOTAL_PAGES }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                css={p === currentPage ? pageButtonActive : pageButton}
                type="button"
                onClick={() => setCurrentPage(p)}
              >
                {p}
              </button>
            ))}
            <button
              css={arrowButton}
              type="button"
              disabled={currentPage === TOTAL_PAGES}
              onClick={() => setCurrentPage(p => p + 1)}
              aria-label="Next page"
            >
              <svg width="10" height="18" viewBox="0 0 10 18" fill="none">
                <path
                  d="M1 5L5 9L1 13"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}
      </>
    );
  }

  if (isSideNav) {
    return (
      <div css={pageSideNav}>
        <SideNav />
        <div css={sideNavMain}>{renderContent()}</div>
      </div>
    );
  }

  return (
    <div css={isShell ? pageShell : page}>
      <TopNav />

      <div css={isShell ? contentShell : content}>{renderContent()}</div>
    </div>
  );
}
